import React , {useState , useEffect , useMemo} from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { Box, Divider, LinearProgress, Skeleton, Typography } from '@mui/material'

import { useServices } from 'contexts';
import { getMyPreparationList } from 'services';
import { WhiteBox, WhiteBoxHeader } from 'components/AppShell'
import { SearchTextField } from 'components/CustomFields'
import { NoResultComponent } from 'components/Home/TodoList';
import HorizontalScrollNavigator from 'components/HorizontalScrollNavigator'
import Chip from 'components/Chip'
import { getTwoDigitNo, snakeCaseToSentence } from 'utils';

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(7.5),
    padding: theme.spacing(5,5,8,5),
    borderRadius: theme.spacing(2),
    border: `2px solid ${theme.palette.neutral['clr-200']}`,
    minWidth: '428px',
    cursor: 'pointer',
    "&:hover": {
      border: `2px solid ${theme.palette.primary['clr-100']}`,
    },
    position: 'relative'
  },
  categoryTag : {
    width : '40px',
    height : '40px',
    position : 'absolute',
    right : '20px',
    top : '20px'
  },
  completionContainer : {
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(2.5)
  }
}))

export const SkeletonCard = () => {
  const classes = useStyles();

  return (
    <Box className={classes.cardContainer}>
      <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '4px' }} />
      <Box className={classes.categoryTag}>
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
      <Box display="flex" flexDirection="column" gap="20px">
        <Skeleton variant="text" width="70%" height={28} />
        <Box className={classes.completionContainer}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="rectangular" width="100%" height={5} sx={{ borderRadius: 4 }} />
        </Box>
      </Box>
    </Box>
  );
};


const CodingInterviewCard = ({template, onClick}) => {
  const classes = useStyles();
  const theme = useTheme();

  const progress = useMemo(() => {
    if(!template.tag) return 0;
    if (template?.totalQuestions && template.totalQuestions > template.totalAttempted) {
      return (template.totalAttempted / template.totalQuestions) * 100;
    }
    return 0;
  }, [template]);
  
  
  return (
    <Box className={classes.cardContainer} onClick={onClick}>
      <Chip
        content={template?.tag ? template?.tag.category === 'company' ? 'Company Interview prep' : 'DSA Prep' : 'Personalized prep'}
        bgColor={theme.palette.tertiary['clr-50']}
        color={theme.palette.neutral['clr-800']}
        typographyVariant='body01-bold'
        sx={{
          borderRadius: '4px',
          padding: '4px'
        }}
      />
      <Box className={classes.categoryTag}>
        <img
          src={
            template?.tag ?
            template?.tag?.category === 'company' 
              ? 'https://assets.languify.in/images/companyTag.png' 
              : 'https://assets.languify.in/images/codeTag.png'
            : "https://assets.languify.in/images/personalizedTag.png"
          } 
          alt='tag'
          width={40}
        />
      </Box>
      <Box display='flex' flexDirection='column' gap='20px'>
        <Typography variant='h3-medium' color='neutral.clr-900' style={{ textTransform: 'capitalize'}}>
          {template?.tag?.name || snakeCaseToSentence(template?.company) || snakeCaseToSentence(template?.companyName)}
        </Typography>

        {
          template?.tag ?
          <Box className={classes.completionContainer}>
            <Typography variant='body01-bold' color='neutral.clr-700'>
              Completed: {template?.totalAttempted}/{template?.totalQuestions}
            </Typography>
            <Box className={classes.progressBar}>
              <LinearProgress 
                variant="determinate"
                value={progress}
                sx={{
                  height: 5,
                  borderRadius: 4,
                  backgroundColor: theme.palette.neutral["clr-200"],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: theme.palette.secondary["clr-900"],
                  },
                }}
              />
            </Box>
          </Box>
          :
          <Box>
            <Typography variant='body01-bold' color='neutral.clr-600'>
              Total topic: {getTwoDigitNo(template?.details?.subTopics?.length)}
            </Typography>
          </Box>
        }
      </Box>
    </Box>
  )
}

const PreparationList = ({ onClick=()=>{} }) => {
  const service = useServices();

  const [loading, setLoading] = useState(false);
  const [myPreparationList, setMyPreparationList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchMyPreparationList = async () => {
      try {
        setLoading(true);
        const result = await service.callService(getMyPreparationList);

        setMyPreparationList(result);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMyPreparationList();
  }, []);

  const filteredList = useMemo(() =>
    myPreparationList?.filter((template) =>
      template?.tag?.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      template?.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) 
    ),
    [myPreparationList, searchQuery]
  );

  return (
    <WhiteBox>
      <WhiteBoxHeader
        id="my-preparation-list"
        heading="My preparation"
        subheading="Your ongoing preparation for companies, DSA topics & personalized coding challenges"
      >
        <SearchTextField
          placeholder="Search here..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={!myPreparationList?.length}
        />
      </WhiteBoxHeader>
      <Divider />
      <Box height={280} mt={5}>
        {
          loading 
            ? (
              <Box display="flex" gap={4} overflow="auto">
                {[...Array(2)].map((_, idx) => (
                  <SkeletonCard key={idx} />
                ))}
              </Box>
            )
        : 
            filteredList.length ? (
              <HorizontalScrollNavigator
                childrensLength={1} scrollWidth={800} 
                showNavigator={filteredList?.length <= 2 ? false : true}
              >
                {filteredList.map((template, index) => (
                  <CodingInterviewCard 
                    key={index} 
                    template={template} 
                    onClick={()=> onClick(template)}
                  />
                ))}
              </HorizontalScrollNavigator>
            ) : (
              <NoResultComponent
                description={
                  <>
                    Nothing to show here! <br />
                    Attempt any challenge inside company or DSA topics.
                  </>
                }
                image='https://assets.languify.in/images/noPreparation.png'
              />
           )
        }
       
       
      </Box>
    </WhiteBox>
  );
};

export default PreparationList;

